/*
    taxonomy-classes.js

    usage:  search.taxonomies[tcare-taxonomy-1].forest.$items       // $ store shortcut

    Note: a new TaxonomiesObject will already contain these (empty) objects:

        TCARE_TAX_1 = new TaxonomyObject('Categories');
        DEMOGRAPHICS = new TaxonomyObject('Demographics');
        VIRTUAL = new TaxonomyObject('Virtual');

    These will be populated after the call to loadMasterTaxonomiesFromApi()

*/


import ForestObject from './forest-classes.js';

// use these ids throughout
export const TCARE_TAX_1 = 'tcare-taxonomy-1';
export const OPEN_ELIG_2022 = 'open-eligibility-2022';
export const DEMOGRAPHICS = 'demographics';
export const VIRTUAL = 'virtual';

// for looping taxonomies - no VIRTUAL includes
const TAXONOMY_IDS = [TCARE_TAX_1, OPEN_ELIG_2022, DEMOGRAPHICS, VIRTUAL];

export class TaxonomyObject {
    constructor (name) {
        this.name = name;
        this.color = '#aaaaaa'
        this.forest = new ForestObject(this);

        // 06/07 - support for public TaxonomyMenu system
        // these should really be called currentMenuNode0, etc.
        this.currentNode0 = null;
        this.currentNode1 = null;
        this.currentNode2 = null;
        this.currentNode3 = null;
        this.currentNode4 = null;

        // this.initForestItems();
    }

    setForestItems(items) {
        this.forest.setItems(items);
    }
    
    getForestItems() {
        return this.forest.getItems();
    }
    
    copyToForestItems(taxonomy) {
        let copiedItems = JSON.parse(JSON.stringify(taxonomy.forest.items));
        this.setForestItems(copiedItems);
    }
    
    // caution - this creates "first-child" node under Category nodes
    initForestItems() {
        this.forest.initItems();
    }

    getAllNodes() {
        return this.forest.getAllNodes();
    }
    
    getSelectedNodes() {
        return this.forest.getSelectedNodes();
    }

    // new 10/17
    getFilteredNodes() {
        return this.forest.getFilteredNodes();
    }

    getAllKeywords() {
        return this.forest.getAllKeywords();
    }

    getSelectedKeywords() {
        return this.forest.getSelectedKeywords();
    }    

    getAllLabels() {
        return this.forest.getAllLabels();
    }


    selectAllNodes(isSelected) {
        this.forest.selectAllNodes(isSelected);
    }

    // new 10/17 - similar to selectAllNodes()
    filterAllNodes(isFiltered) {
        this.forest.filterAllNodes(isFiltered);
    }

    // new 8/23 for resetting from the DataTaxonomyField
    selectKeywordNodes(keywordsArray) {
        this.selectAllNodes(false);
        for (const keyword of keywordsArray) {
            let node = this.findNodeByKeyword(keyword);
            if (node) {
                node.isSelected = true;
                // alert('selected node: ' + node.keyword);
            }
        }
    }

    // new 10/17
    // if a node is in the keywords array set .isFiltered
    filterByKeywords(keywordsArray) {
        //console.log(this.name + '.filterByKeywords');
        this.selectAllNodes(false);
        this.filterAllNodes(false);
        for (const keyword of keywordsArray) {
            let node = this.findNodeByKeyword(keyword);
            if (node) {
                node.isFiltered = true;
                // node.isSelected = true;      // NO! chips and checks will be set!
            }
        }
    }

    

    findNodeByKeyword(keyword) {
        let allNodes = this.getAllNodes();
        return allNodes.find(node => node.keyword === keyword);
    }

    // new 6/29 - must map keywords to multiple menu nodes
    findNodesByKeyword(keyword) {
        let allNodes = this.getAllNodes();
        return allNodes.filter(node => node.keyword === keyword);
    }

    // used for mapping menu breadcrumb to actual taxonomy nodes
    findNodeByLabel(label) {
        let allNodes = this.getAllNodes();
        return allNodes.find(node => node.label === label);
    }

    findNodesByLabel(label) {
        let allNodes = this.getAllNodes();
        return allNodes.filter(node => node.label === label);
    }

    // new 06/14
    clearServiceIds() {
        this.forest.clearServiceIds();
    }  
        
    getTopLevelItems() {
        return this.forest.getTopLevelItems();
    }


    // new 10/17 - used for the filtered TaxonomyCheckboxMenu
    // should be recursive but want to get this done now!
    hasFilteredChildren(node) {
        // console.log('hasFilteredChildren: ' + node.label);
        // need to check more levels here!

        if (node.children) {
            for (const child1 of node.children) {
                if (child1.isFiltered) return true;
                if (child1.children) {
                    for (const child2 of child1.children) {
                        if (child2.isFiltered) return true;
                        if (child2.children) {
                            for (const child3 of child2.children) {
                                if (child3.isFiltered) return true;
                                if (child3.children) {
                                    for (const child4 of child3.children) {
                                        if (child4.isFiltered) return true;
                                    }
                                }
                            }
                        }
                    }
                }

            }
        }
        return false;
    }

    getFilteredChildrenCount(node) {
        // console.log(this.name + '.getFilteredChildrenCount for ' + node.label);
        // need to check more levels here!
        let count = 0;

        if (node.isFiltered) count++;
        if (node.children) {
            for (const child1 of node.children) {
                if (child1.isFiltered) count++;
                if (child1.children) {
                    for (const child2 of child1.children) {
                        if (child2.isFiltered) count++;
                        if (child2.children) {
                            for (const child3 of child2.children) {
                                if (child3.isFiltered) count++;
                                if (child3.children) {
                                    for (const child4 of child3.children) {
                                        if (child4.isFiltered) count++;
                                    }
                                }
                            }
                        }
                    }
                }

            }
        }

        // console.log('returning count: ' + count);
        return count;
    }  
        
}




// NOTE: this needs to be edited as new Taxonomies are added!
export class TaxonomiesObject {
    constructor () {
        this[TCARE_TAX_1] = new TaxonomyObject('Categories');
        this[OPEN_ELIG_2022] = new TaxonomyObject('Open Eligibility');
        this[DEMOGRAPHICS] = new TaxonomyObject('Demographics');
        this[VIRTUAL] = new TaxonomyObject('Virtual');

        // these UI props can be preset here
        this[TCARE_TAX_1].color = 'purple';
        this[OPEN_ELIG_2022].color = 'darkgreen';
        this[DEMOGRAPHICS].color = 'darkblue';
        this[VIRTUAL].color = 'green';
    }

    taxonomy(id) {
        return this[id];
    }

    getTaxonomy(id) {
        return this[id];
    }

    // set all .isSelected = false, .isClosed = true, colors, etc.
    // called once at startup by master-objects.js
    initTaxonomies(apiData) {

        for (const taxId of TAXONOMY_IDS) {
            if (apiData[taxId]) {
                this[taxId].setForestItems(apiData[taxId]);
                this[taxId].initForestItems();
            } else {
                // set/throw error?
                console.warn('initTaxonomies warning - no "' + taxId + '" present in API data');
            }
        }

        // does the front end even use this?
        // disabled 10/17/22

        // if (apiData[VIRTUAL]) {
        //     this[VIRTUAL].setForestItems(apiData[VIRTUAL]);
        //     this[VIRTUAL].initForestItems();
        // } else {
        //     // set/throw error?
        //     console.warn('initTaxonomies warning - no virtual present in API data');
        // }

    }

    // no virtuals are included here
    getAllNodes() {
        let nodes = [];
        for (const taxId of TAXONOMY_IDS) {
            for (const node of this[taxId].getAllNodes()) {
                nodes.push(node);
            }    
        }
        return nodes;
    }
    
    // no virtuals are included here
    getSelectedNodes() {
        let nodes = [];
        for (const taxId of TAXONOMY_IDS) {
            for (const node of this[taxId].getSelectedNodes()) {
                nodes.push(node);
            }    
        }
        return nodes;
    }

    // no virtuals are included here
    getAllKeywords() {
        let keywords = [];
        for (const taxId of TAXONOMY_IDS) {
            for (const keyword of this[taxId].getAllKeywords()) {
                keywords.push(keyword);
            }    
        }
        return keywords;
    }

    // no virtuals are included here
    getSelectedKeywords() {
        let keywords = [];
        for (const taxId of TAXONOMY_IDS) {
            for (const keyword of this[taxId].getSelectedKeywords()) {
                keywords.push(keyword);
            }    
        }
        return keywords;
    }    

    findNodeByLabel(label) {
        let allNodes = this.getAllNodes();
        return allNodes.find(node => node.label == label);
    }

    findNodeByKeyword(keyword) {
        //console.log('TaxonomiesObject.findNodeByKeyword: ', keyword);
        let allNodes = this.getAllNodes();
        //console.log('TaxonomiesObject searching allNodes: ', allNodes);
        return allNodes.find(node => node.keyword == keyword);
    }

    selectAllNodes(isSelected) {
        for (const taxId of TAXONOMY_IDS) {
            this[taxId].selectAllNodes(isSelected);
        }
    }    

}

export default TaxonomiesObject;
